// src/components/messaging/ReactionPicker.tsx
import React from 'react';
import { Box, IconButton, Tooltip, Popover, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Available reactions with emojis
export const REACTIONS = [
  { key: 'like', emoji: '👍', label: 'Like' },
  { key: 'love', emoji: '❤️', label: 'Love' },
  { key: 'pray', emoji: '🙏', label: 'Pray' },
  { key: 'amen', emoji: '✝️', label: 'Amen' },
  { key: 'cross', emoji: '☦️', label: 'Cross' },
] as const;

export type ReactionKey = typeof REACTIONS[number]['key'];

interface ReactionPickerProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSelect: (reaction: ReactionKey) => void;
  selectedReaction?: string;
}

const ReactionPicker: React.FC<ReactionPickerProps> = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  selectedReaction,
}) => {
  const { t } = useTranslation();

  const handleSelect = (reaction: ReactionKey) => {
    onSelect(reaction);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            p: 0.5,
            backgroundColor: 'background.paper',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 0.25,
          p: 0.5,
        }}
      >
        {REACTIONS.map(({ key, emoji, label }) => (
          <Tooltip key={key} title={t(`messaging.reaction.${key}`, label)} arrow>
            <IconButton
              onClick={() => handleSelect(key)}
              sx={{
                width: 36,
                height: 36,
                fontSize: '1.25rem',
                backgroundColor: selectedReaction === key
                  ? (theme) => alpha(theme.palette.primary.main, 0.15)
                  : 'transparent',
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  transform: 'scale(1.2)',
                },
                transition: 'all 0.15s ease',
              }}
            >
              {emoji}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    </Popover>
  );
};

export default ReactionPicker;
